

import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { useCandidateForm } from '@/hooks/useCandidateForm';
import { LoadingState } from '@/components/submit-referents/LoadingState';
import { ErrorState } from '@/components/submit-referents/ErrorState';
import { SuccessState } from '@/components/submit-referents/SuccessState';
import { RefusalConfirmationState } from '@/components/submit-referents/RefusalConfirmationState';
import { AlreadyRespondedState } from '@/components/submit-referents/AlreadyRespondedState';
import { CandidateDecisionForm } from '@/components/submit-referents/CandidateDecisionForm';
import { OptimizedReferentsForm } from '@/components/submit-referents/OptimizedReferentsForm';
import { PageWrapper } from '@/components/layout/PageWrapper';

const SubmitReferents = () => {
  const { token } = useParams<{ token: string }>();
  const {
    campaign,
    candidateStatus,
    branding,
    loading,
    error,
    isSubmitting,
    isSubmitted,
    showDecisionForm,
    handleAccept,
    handleRefuse,
    onSubmit,
    refresh
  } = useCandidateForm(token);

  if (loading) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="font-sans"><LoadingState /></div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="font-sans"><ErrorState error={error} token={token} /></div>
      </PageWrapper>
    );
  }

  // Vérifier d'abord si les référents ont été soumis avec succès
  if (isSubmitted) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="font-sans"><SuccessState campaignId={campaign?.id} /></div>
      </PageWrapper>
    );
  }

  // Afficher l'état approprié basé sur le statut du candidat
  if (candidateStatus) {
    // Le candidat a complété le processus (terminé définitivement)
    if (candidateStatus.status === 'completed') {
      return (
        <PageWrapper layout="no-sidebar">
          <div className="font-sans"><AlreadyRespondedState candidateStatus={candidateStatus} /></div>
        </PageWrapper>
      );
    }
    
    // Le candidat a refusé - afficher une option pour changer d'avis
    if (candidateStatus.status === 'refused') {
      return (
        <PageWrapper layout="no-sidebar">
          <div className="font-sans">
            <RefusalConfirmationState 
              candidateStatus={candidateStatus} 
              onChangeDecision={async () => {
                // Annuler la réponse précédente et recharger
                if (token) {
                  await import('@/services/campaignService').then(async (service) => {
                    await service.cancelCandidateResponse(token);
                    // OPTIMISATION: Utiliser refresh au lieu de window.location.reload()
                    await refresh();
                  });
                }
              }}
            />
          </div>
        </PageWrapper>
      );
    }
    
    // Le candidat a accepté mais n'a pas encore soumis ses référents
    if (candidateStatus.status === 'accepted') {
      // OPTIMISATION: Plus besoin de vérifier si campaign existe car tout est chargé d'un coup
      return (
        <PageWrapper layout="no-sidebar">
          <div className="font-sans">
            <OptimizedReferentsForm
              campaign={campaign || {} as any}
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
            />
          </div>
        </PageWrapper>
      );
    }
  }

  // Le candidat n'a pas encore répondu - afficher le formulaire de décision
  if (showDecisionForm && candidateStatus) {
    // OPTIMISATION: Plus besoin de vérifier si campaign existe car tout est chargé d'un coup
    return (
      <PageWrapper layout="no-sidebar">
        <div className="font-sans">
          <CandidateDecisionForm
            campaign={{
              candidate_name: candidateStatus.candidate_name,
              target_position: candidateStatus.target_position,
              ai_extracted_data: campaign?.ai_extracted_data,
              referents_requested_count: campaign?.referents_requested_count
            }}
            branding={branding}
            onAccept={handleAccept}
            onRefuse={handleRefuse}
            isSubmitting={isSubmitting}
          />
        </div>
      </PageWrapper>
    );
  }

  // Cas de fallback - campagne introuvable
  if (!campaign && !candidateStatus) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center font-sans">
          <Card className="w-full max-w-md">
            <CardContent className="text-center p-8">
              <p className="text-gray-600">Campagne introuvable</p>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  // Ne devrait jamais arriver avec la nouvelle logique
  return (
    <PageWrapper layout="no-sidebar">
      <div className="min-h-screen bg-gray-50 flex items-center justify-center font-sans">
        <Card className="w-full max-w-md">
          <CardContent className="text-center p-8">
            <p className="text-gray-600">Chargement en cours...</p>
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
};

export default SubmitReferents;
